export default function TenantEstoppelCertificates() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-file-shield-line"></i>
            <span>Legal Documentation</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Tenant Estoppel Certificates: What They Are and Why They Matter
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Estoppel certificates are critical documents that protect buyers from undisclosed lease terms and tenant claims. Understanding and obtaining them is essential for closing your sale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">What is a Tenant Estoppel Certificate?</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              A tenant estoppel certificate (also called an estoppel letter or tenant certification) is a legally binding document signed by a tenant that confirms the key terms of their lease and current status of their tenancy. The word "estoppel" means the tenant is legally prevented from later claiming different terms than what they certified.
            </p>

            <p className="text-slate-700 mb-6 leading-relaxed">
              This document protects the buyer by ensuring that what the seller represents about the leases matches what the tenants themselves confirm. It's one of the most important due diligence documents in any apartment building sale, and most buyers will require estoppel certificates from all tenants before closing.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Why Buyers Require Estoppels</h4>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Without estoppel certificates, buyers rely solely on the seller's representations about lease terms. If a tenant later claims different terms (lower rent, special concessions, verbal agreements), the buyer has no recourse. Estoppels eliminate this risk by getting tenant confirmation in writing.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Key Information in Estoppel Certificates</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Lease Commencement and Expiration Dates</p>
                  <p className="text-slate-600 text-xs">Confirms when the lease started and when it ends, including any renewal options or extensions</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Current Rent Amount and Payment Status</p>
                  <p className="text-slate-600 text-xs">States the monthly rent, when it's due, and confirms tenant is current on payments (or discloses any arrears)</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Security Deposit Amount</p>
                  <p className="text-slate-600 text-xs">Confirms the exact amount of security deposit held by landlord and any interest accrued</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Prepaid Rent or Credits</p>
                  <p className="text-slate-600 text-xs">Discloses any prepaid rent, rent credits, or concessions (like "first month free")</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Lease Modifications or Amendments</p>
                  <p className="text-slate-600 text-xs">Confirms whether any changes have been made to the original lease agreement</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Outstanding Maintenance Requests</p>
                  <p className="text-slate-600 text-xs">Discloses any pending repair requests or landlord obligations that haven't been fulfilled</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Landlord Defaults or Violations</p>
                  <p className="text-slate-600 text-xs">States whether landlord is in default of any lease terms or has violated any provisions</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Verbal Agreements or Side Deals</p>
                  <p className="text-slate-600 text-xs">Confirms no verbal agreements exist beyond what's written in the lease</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Utilities and Services Included</p>
                  <p className="text-slate-600 text-xs">Clarifies which utilities or services are included in rent versus paid separately by tenant</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/5a10bfe0e5cbe48686fbf858c6b1d916.jpg"
                  alt="Tenant estoppel certificate signing and lease verification process"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-time-line text-teal-600 text-xl"></i>
                Estoppel Certificate Timeline
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Request (Day 1-3)</p>
                    <p className="text-slate-600 text-xs">Send estoppel forms to all tenants with clear instructions and deadline</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Follow-up (Day 7-10)</p>
                    <p className="text-slate-600 text-xs">Contact non-responsive tenants via phone, email, and door notices</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Collection (Day 14-21)</p>
                    <p className="text-slate-600 text-xs">Gather completed certificates and review for accuracy and completeness</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                    4
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Delivery (Day 21-30)</p>
                    <p className="text-slate-600 text-xs">Provide all certificates to buyer for review during due diligence</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-4">Estoppel Certificate Statistics</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-600">Typical Response Rate</span>
                    <span className="text-sm font-semibold text-slate-900">60-75%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-teal-600 h-2 rounded-full" style={{ width: '70%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-600">With Incentives</span>
                    <span className="text-sm font-semibold text-slate-900">85-95%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-600">Discrepancies Found</span>
                    <span className="text-sm font-semibold text-slate-900">15-25%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-amber-600 h-2 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">How to Get Tenants to Complete Estoppel Certificates</h3>
          
          <p className="text-slate-700 mb-6 leading-relaxed">
            Getting tenants to complete and return estoppel certificates can be challenging. Many tenants don't understand their importance or are simply too busy. Here are proven strategies to maximize response rates:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-gift-line text-green-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Offer Incentives</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Provide $25-$100 gift cards, rent credits, or small cash payments for completed certificates. This dramatically increases response rates from 60% to 90%+.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Example:</strong> "Complete and return this form within 10 days to receive a $50 Amazon gift card as our thank you."
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-file-list-3-line text-blue-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Simplify the Form</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Pre-fill as much information as possible. Tenants should only need to verify accuracy and sign. Complex forms get ignored.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Tip:</strong> Use checkboxes and "confirm/deny" format rather than requiring tenants to write out information.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-chat-3-line text-purple-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Personal Communication</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Don't just mail forms. Knock on doors, make phone calls, and explain why it's important. Personal touch increases compliance significantly.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Script:</strong> "This helps ensure your lease terms are protected with the new owner. It only takes 5 minutes."
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-smartphone-line text-teal-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Multiple Submission Options</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Allow tenants to submit via email, text photo, drop box, or in-person. Make it as easy as possible with multiple convenient options.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Options:</strong> Email PDF, text photo of signed form, drop in office mailbox, or schedule pickup.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-alarm-line text-amber-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Create Urgency</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Set clear deadlines and explain consequences of non-compliance. Follow up multiple times as deadline approaches.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Message:</strong> "Required by [date] for property sale. Delays may affect closing and your lease transfer."
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-red-600 text-xl"></i>
                </div>
                <h4 className="font-semibold text-slate-900">Emphasize Protection</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Explain that the estoppel protects THEM by ensuring the new owner honors their lease terms exactly as stated.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Benefit:</strong> "This document protects your rights and ensures your rent and lease terms don't change."
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">What to Do When Tenants Won't Sign</h3>
          
          <p className="text-slate-700 mb-6 leading-relaxed">
            Despite your best efforts, some tenants may refuse to complete estoppel certificates or simply ignore requests. Here's how to handle non-responsive tenants:
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-file-text-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Review Lease for Estoppel Requirement</h4>
                <p className="text-slate-600 text-sm mb-2">
                  Many leases include clauses requiring tenants to complete estoppel certificates upon request. If your lease has this provision, you can enforce it and potentially charge fees for non-compliance.
                </p>
                <p className="text-xs text-slate-600 italic">
                  Check your lease for language like: "Tenant agrees to execute and deliver estoppel certificates within 10 days of Landlord's request."
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-file-shield-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Seller's Estoppel Certificate</h4>
                <p className="text-slate-600 text-sm mb-2">
                  If tenants won't sign, you (the seller) can provide a seller's estoppel certificate certifying the lease terms to the best of your knowledge. This is less protective for the buyer but better than nothing.
                </p>
                <p className="text-xs text-slate-600 italic">
                  Include language: "Tenant was requested to provide estoppel but failed to respond. Seller certifies the following information is accurate based on lease records."
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Price Adjustment or Escrow Holdback</h4>
                <p className="text-slate-600 text-sm mb-2">
                  Negotiate with the buyer to reduce the purchase price or hold funds in escrow for units without estoppel certificates. Typical holdback is 2-3 months rent per unit.
                </p>
                <p className="text-xs text-slate-600 italic">
                  Example: For a unit with $1,500/month rent, buyer might request $3,000-$4,500 holdback until estoppel is obtained or 90 days post-closing.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-user-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Seller Indemnification</h4>
                <p className="text-slate-600 text-sm">
                  Offer to indemnify the buyer against any claims from tenants who didn't provide estoppels. This means you agree to cover any losses if tenant later claims different lease terms than what you represented.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
